import type { Logging } from '@sbclite/core';

export interface LogFilters {
  level?: Logging.LogLevel;
  service?: string;
  component?: string;
  search?: string;
  startTime?: string;
  endTime?: string;
  asOf?: string;
}

export const LOG_PAGE_SIZE = 50;
export const LOG_MAX_PAGE = 1000;

export interface LogUrlState {
  readonly filters: LogFilters;
  readonly page: number;
  readonly pageSize: number;
  readonly error: string | null;
}

export interface LogDrafts {
  readonly search: string;
  readonly component: string;
}

export function mergeLogDrafts(filters: LogFilters, drafts: LogDrafts, changes: Partial<LogFilters> = {}): LogFilters {
  return { ...filters, search: drafts.search || undefined, component: drafts.component || undefined, ...changes };
}

export function logQueryParams(filters: LogFilters, page: number, pageSize: number): LogFilters & { page: number; pageSize: number } {
  return { ...filters, page, pageSize };
}

const filterKeys = ['level', 'service', 'component', 'search', 'startTime', 'endTime', 'asOf'] as const;

export function filtersFromSearchParams(params: URLSearchParams): LogFilters {
  const result: LogFilters = {};
  for (const key of filterKeys) {
    const value = params.get(key)?.trim();
    if (value) Object.assign(result, { [key]: value });
  }
  return result;
}

function isIso(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value) && Number.isFinite(Date.parse(value));
}

export function readLogUrlState(params: URLSearchParams): LogUrlState {
  const filters = filtersFromSearchParams(params);
  const page = pageFromSearchParams(params);
  const pageSize = pageSizeFromSearchParams(params);
  const rawPage = params.get('page');
  const rawPageSize = params.get('pageSize');
  const rawLevel = params.get('level');
  const levels: readonly string[] = ['debug', 'info', 'warn', 'error', 'fatal'];
  let error: string | null = null;
  const rawPageNumber = rawPage ? Number(rawPage) : 1;
  if (rawPage && (!/^\d+$/.test(rawPage) || rawPageNumber < 1 || rawPageNumber > LOG_MAX_PAGE)) error = `Page must be between 1 and ${LOG_MAX_PAGE}.`;
  else if (rawPageSize && ![25, 50, 100].includes(Number(rawPageSize))) error = 'Page size must be 25, 50, or 100.';
  else if (rawLevel && !levels.includes(rawLevel)) error = 'The selected log level is invalid.';
  else if ((filters.startTime && !isIso(filters.startTime)) || (filters.endTime && !isIso(filters.endTime)) || (filters.asOf && !isIso(filters.asOf))) error = 'The URL contains an invalid ISO time value.';
  else if (filters.startTime && filters.endTime && Date.parse(filters.startTime) >= Date.parse(filters.endTime)) error = 'Start time must be before end time.';
  return { filters, page, pageSize, error };
}

export function writeLogSearchParams(filters: LogFilters, page: number, pageSize: number): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of filterKeys) {
    const value = filters[key];
    if (value) params.set(key, value);
  }
  if (page > 1) params.set('page', String(page));
  if (pageSize !== LOG_PAGE_SIZE) params.set('pageSize', String(pageSize));
  return params;
}

export function pageFromSearchParams(params: URLSearchParams): number {
  const value = Number(params.get('page'));
  return Number.isInteger(value) && value > 0 && value <= LOG_MAX_PAGE ? value : 1;
}

export function pageSizeFromSearchParams(params: URLSearchParams): number {
  const value = Number(params.get('pageSize'));
  return [25, 50, 100].includes(value) ? value : LOG_PAGE_SIZE;
}

export function localDateTimeToIso(value: string): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = '0'] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second), 0);
  if (date.getFullYear() !== Number(year) || date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)
    || date.getHours() !== Number(hour) || date.getMinutes() !== Number(minute) || date.getSeconds() !== Number(second)) return null;
  return date.toISOString();
}

export function isoToLocalDateTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function validateTimeRange(start?: string, end?: string): string | null {
  if (!start && !end) return null;
  const startIso = start ? localDateTimeToIso(start) : null;
  const endIso = end ? localDateTimeToIso(end) : null;
  if ((start && !startIso) || (end && !endIso)) return 'Enter valid dates in your local timezone.';
  if (startIso && endIso && new Date(startIso).getTime() >= new Date(endIso).getTime()) return 'Start time must be before end time.';
  return null;
}

export function safeJson(value: unknown, maxLength = 12000): { text: string; truncated: boolean } {
  try {
    const seen = new WeakSet<object>();
    const text = JSON.stringify(value, (_key, entry: unknown) => {
      if (typeof entry === 'object' && entry !== null) {
        if (seen.has(entry)) return '[Circular]';
        seen.add(entry);
      }
      return entry;
    }, 2) ?? String(value);
    return text.length > maxLength ? { text: `${text.slice(0, maxLength)}\n…`, truncated: true } : { text, truncated: false };
  } catch {
    return { text: '[Unable to render context]', truncated: false };
  }
}
